import React, { useEffect, useRef, useState, useCallback } from "react";
import ChaloLoader from "../../widgets/chaloLoader";
import { useHlsPlayer } from "./hooks/useHlsPlayer";
import styles from "./HlsVideoPlayer.module.css";

import PropTypes from "prop-types";

const DEFAULT_ERROR = "RTMP feed not available due to server issue.";

const HlsVideoPlayer = ({ src, onLiveStatusChange, isModal = true, onError }) => {
  const videoRef = useRef(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isLive, setIsLive] = useState(false);
  const [errorMessage, setErrorMessage] = useState(DEFAULT_ERROR);
  const [retryCount, setRetryCount] = useState(0);

  const { initPlayer, cleanupPlayer } = useHlsPlayer(
    videoRef,
    retryCount,
    setRetryCount,
    setErrorMessage,
    setIsLive,
    setIsLoading
  );

  useEffect(() => {
    if (!src) {
      setErrorMessage("Live feed URL not provided.");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setIsLive(false);
    setErrorMessage(null);
    initPlayer(src);

    return cleanupPlayer;
  }, [src, retryCount]);

  useEffect(() => {
    onLiveStatusChange?.(isLive);
  }, [isLive]);

  // Notify parent of error state (for non-modal, parent handles retry)
  useEffect(() => {
    if (!isLive && !isLoading && errorMessage) {
      onError?.(true, errorMessage);
    }
  }, [errorMessage, isLive, isLoading, onError]);

  const handleRetry = useCallback(() => {
    setRetryCount((p) => p + 1);
  }, []);

  return (
    <div className={styles.wrapper}>
      <div className={styles.videoContainer}>
        <video
          ref={videoRef}
          className={styles.video}
          playsInline
          muted
          autoPlay
          controls={false}
        />

        {isLoading && <div className={styles.loader}><ChaloLoader /></div>}

        {!isLive && !isLoading && (
          <div className={styles.errorOverlay}>
            <p>{errorMessage}</p>
            {isModal && (
              <button className={styles.retryBtn} onClick={handleRetry}>
                Retry
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default HlsVideoPlayer;

HlsVideoPlayer.propTypes = {
  src: PropTypes.string,
  onLiveStatusChange: PropTypes.func,
  isModal: PropTypes.bool,
  onError: PropTypes.func,
};
